import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Broadcast, ArrowLeft, PaperPlaneRight, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import api from "../lib/api";
import { youtubeEmbed } from "../lib/youtube";
import VideoWatermark from "../components/VideoWatermark";

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);
  if (!target) return null;
  const t = new Date(target).getTime();
  if (isNaN(t)) return null;
  const diff = t - now;
  if (diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s };
}

export default function Live() {
  const { t } = useLang();
  const [live, setLive] = useState(null);
  const [ready, setReady] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const listRef = useRef(null);

  const fetchLive = () => api.get("/live").then((res) => setLive(res.data)).catch(() => {}).finally(() => setReady(true));

  useEffect(() => { fetchLive(); }, []);

  // Presence heartbeat: authenticated users on /live ping every 20s so admin can see the count.
  useEffect(() => {
    const ping = () => api.post("/live/presence").catch(() => {});
    ping();
    const i = setInterval(ping, 20000);
    return () => clearInterval(i);
  }, []);

  const chatOn = live?.chat_enabled;
  useEffect(() => {
    if (!chatOn) return;
    let alive = true;
    const load = () => api.get("/live/messages").then((res) => { if (alive) setMessages(res.data); }).catch(() => {});
    load();
    const i = setInterval(load, 8000);
    return () => { alive = false; clearInterval(i); };
  }, [chatOn]);

  useEffect(() => { listRef.current?.scrollTo(0, listRef.current.scrollHeight); }, [messages]);

  const embed = live?.url ? youtubeEmbed(live.url) : null;
  const isLive = live?.enabled && embed;
  const hasGoproLive = live?.enabled && !!live?.gopro_live_url;
  const countdown = useCountdown(!isLive && !hasGoproLive ? live?.scheduled_at : null);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post("/live/messages", { text });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    }
  };

  return (
    <div data-testid="live-page" className="min-h-screen px-6 pt-28 pb-28 md:px-10">
      <div className="mx-auto max-w-6xl">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-400 transition-colors hover:text-cyan">
          <ArrowLeft size={16} /> {t("home")}
        </Link>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="flex items-center gap-3">
            <span className="relative flex h-3 w-3">
              {(isLive || hasGoproLive) && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-70" />}
              <span className={`relative inline-flex h-3 w-3 rounded-full ${(isLive || hasGoproLive) ? "bg-red-500" : "bg-slate-600"}`} />
            </span>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan">— {(isLive || hasGoproLive) ? "Live Now" : "Live"}</p>
          </div>
          <h1 className="mt-4 font-head text-4xl font-black uppercase tracking-tighter text-white md:text-6xl">
            {live?.title || "Live Session"}
          </h1>
        </motion.div>

        <div className={`mt-10 grid grid-cols-1 gap-6 ${isLive && chatOn ? "lg:grid-cols-3" : ""}`}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className={isLive && chatOn ? "lg:col-span-2" : ""}>
            {!ready ? (
              <div className="flex aspect-video items-center justify-center border border-white/10 bg-ink-800 font-mono text-sm text-slate-500">Loading…</div>
            ) : isLive ? (
              <div className="relative aspect-video w-full overflow-hidden border border-white/10 bg-black">
                <iframe data-testid="live-iframe" src={`${embed}?autoplay=1&rel=0&modestbranding=1`} title="Live stream" className="absolute inset-0 h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen />
                {/* Overlay hides YouTube's real-time viewer count (top-right of the player chrome) */}
                <div data-testid="viewer-count-mask" className="pointer-events-none absolute right-0 top-0 z-10 h-11 w-40 bg-black" aria-hidden="true" />
                {/* WBA branding watermark — cycles across the 4 corners every 20s */}
                <VideoWatermark testId="live-watermark" />
              </div>
            ) : hasGoproLive ? (
              <div data-testid="live-gopro" className="flex aspect-video flex-col items-center justify-center gap-5 border border-red-500/30 bg-gradient-to-br from-ink-800/70 to-ink-900 px-6 text-center">
                <span className="relative flex h-3 w-3">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-70" />
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
                </span>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-400">● Live in corso su GoPro</p>
                <h2 className="max-w-lg font-head text-2xl font-black uppercase tracking-tight text-white md:text-3xl">
                  {live?.title || "Diretta in corso"}
                </h2>
                <p className="max-w-md font-body text-sm text-slate-400">
                  La sessione è trasmessa in diretta sulla piattaforma GoPro. Clicca il pulsante per aprire lo stream in una nuova scheda.
                </p>
                <a
                  data-testid="gopro-live-btn"
                  href={live.gopro_live_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-3 border border-red-500/60 bg-red-500/10 px-8 py-4 font-mono text-sm font-bold uppercase tracking-widest text-red-300 transition-all hover:border-red-400 hover:bg-red-500/20 hover:text-white"
                >
                  <Broadcast size={18} weight="bold" /> Guarda su GoPro
                </a>
                <p className="mt-1 max-w-md break-all font-mono text-[10px] uppercase tracking-widest text-slate-600">
                  {live.gopro_live_url}
                </p>
              </div>
            ) : (
              <div data-testid="live-offline" className="flex aspect-video flex-col items-center justify-center gap-4 border border-dashed border-white/15 bg-ink-800/50 px-6 text-center">
                <Broadcast size={54} weight="thin" className="text-slate-600" />
                {countdown ? (
                  <>
                    <p className="font-head text-xl text-white">Prossima diretta tra</p>
                    <div data-testid="live-countdown" className="flex items-center gap-4">
                      {[["G", countdown.d], ["H", countdown.h], ["M", countdown.m], ["S", countdown.s]].map(([l, v]) => (
                        <div key={l} className="flex flex-col items-center">
                          <span className="font-head text-4xl font-black text-cyan">{String(v).padStart(2, "0")}</span>
                          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">{l}</span>
                        </div>
                      ))}
                    </div>
                    {live?.scheduled_at && <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{new Date(live.scheduled_at).toLocaleString()}</p>}
                  </>
                ) : (
                  <>
                    <p className="font-head text-xl text-slate-300">Nessuna diretta al momento</p>
                    <p className="max-w-md font-body text-sm text-slate-500">Torna durante le sessioni di chirurgia in diretta programmate.</p>
                    <Link to="/library/video" className="mt-2 border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:border-cyan hover:text-cyan">{t("browse_library")}</Link>
                  </>
                )}
              </div>
            )}
          </motion.div>

          {isLive && chatOn && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} data-testid="live-chat" className="flex h-[420px] flex-col border border-white/10 bg-ink-800/50 lg:h-auto">
              <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
                <ChatCircleDots size={18} className="text-green-400" />
                <span className="font-mono text-[11px] uppercase tracking-widest text-white">Q&amp;A</span>
                <span className="ml-auto h-2 w-2 rounded-full bg-green-500" />
              </div>
              <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto p-4">
                {messages.length === 0 && <p className="font-body text-sm text-slate-600">Fai la prima domanda…</p>}
                {messages.map((m) => (
                  <div key={m.id} data-testid={`chat-msg-${m.id}`}>
                    <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: m.is_admin ? "#00F0FF" : "#64748b" }}>{m.user_name}{m.is_admin ? " · staff" : ""}</p>
                    <p className="font-body text-sm text-slate-200">{m.text}</p>
                  </div>
                ))}
              </div>
              <form onSubmit={send} className="flex items-center gap-2 border-t border-white/10 p-3">
                <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi una domanda…" className="flex-1 border border-white/15 bg-ink-900/60 px-3 py-2 font-body text-sm text-white outline-none focus:border-cyan" />
                <button data-testid="chat-send" type="submit" className="bg-cyan p-2.5 text-ink-900 transition-colors hover:bg-white"><PaperPlaneRight size={16} weight="bold" /></button>
              </form>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
